import type { ReactElement } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

export type SectionStateStrings = Readonly<{
  loading: string;
  error: string;
  empty: string;
}>;

type DashboardSectionStateProps = Readonly<{
  id: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  strings: SectionStateStrings;
  unavailableMessage?: string | null;
  errorMessage?: string | null;
  dropped?: number;
  children: ReactElement;
}>;

export const DashboardSectionState = ({
  id,
  isLoading,
  isError,
  isEmpty,
  strings,
  unavailableMessage = null,
  errorMessage = null,
  dropped = 0,
  children,
}: DashboardSectionStateProps) => {
  const notice = (() => {
    if (isError) return noticeForError(errorMessage, strings.error);
    if (unavailableMessage !== null) return unavailableMessage;
    if (isLoading) return strings.loading;
    if (isEmpty) return strings.empty;

    return null;
  })();

  if (notice === null) {
    if (dropped <= 0) return children;

    return (
      <>
        <Box id={`${id}-incomplete`} sx={warningSx} role="status">
          <Typography variant="text-sm-regular">
            {appStrings.modules.coachingDashboard.institutionAdmin.incompleteList(dropped)}
          </Typography>
        </Box>
        {children}
      </>
    );
  }

  return (
    <Box id={id} sx={noticeSx} role={isError ? 'alert' : 'status'}>
      <Typography variant="text-sm-regular">{notice}</Typography>
    </Box>
  );
};

const warningSx: SxProps<Theme> = ({ backrs }) => ({
  paddingY: backrs.spacing['spacing-lg'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const noticeSx: SxProps<Theme> = ({ backrs }) => ({
  paddingY: backrs.spacing['spacing-3xl'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
